import { useCallback, useMemo, useState } from 'react';

import { toPageInfo } from '@fly/shared';
import { useInfiniteQueryViewState } from '@fly/shared-hooks';
import { useServerTableState } from '@fly/shared-ui';

import {
  type ArtifactDetails,
  type ArtifactVersion,
  useGetArtifactVersionsInfiniteQuery,
} from '../../store/registry.service';
import { useArtifactVersionsColumns } from './useArtifactVersionsColumns';

const PAGE_SIZE = 50;

type UseArtifactVersionsParams = {
  artifactName: string;
  packageType: string;
  isRepositoriesLoading?: boolean;
};

export function useArtifactVersions({
  artifactName,
  packageType,
  isRepositoriesLoading,
}: UseArtifactVersionsParams) {
  const { search, handleStateChange, isSearchPending } = useServerTableState({
    paginationType: 'virtual_scroll',
    searchDebounceMs: 300,
  });

  const queryArgs = useMemo(
    () => ({
      name: artifactName,
      packageType,
      versionsFirst: PAGE_SIZE,
      versionsSearch: search?.trim() || undefined,
    }),
    [artifactName, packageType, search]
  );

  const queryResult = useGetArtifactVersionsInfiniteQuery(queryArgs);

  const combinePages = useCallback((pages: ArtifactDetails[]) => {
    const firstArtifact = pages[0]?.[0];
    if (!firstArtifact) return undefined;

    const lastArtifact = pages.at(-1)?.[0] ?? firstArtifact;

    return {
      artifact: {
        ...firstArtifact,
        versions: pages.flatMap(
          (page) => (page[0]?.versions ?? []) as ArtifactVersion[]
        ),
        versionsPageInfo: toPageInfo(lastArtifact.versionsPageInfo),
      },
    };
  }, []);

  const {
    combinedData,
    isDataLoading,
    shouldRedirect,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQueryViewState({
    queryArgs,
    queryResult,
    combinePages,
    isSearchPending,
    extraLoading: Boolean(isRepositoriesLoading),
  });

  const [clientFilteredCount, setClientFilteredCount] = useState<number | null>(
    null
  );

  const versions = combinedData?.artifact.versions ?? [];
  const { columns, searchKey } = useArtifactVersionsColumns(
    versions,
    isDataLoading,
    clientFilteredCount ?? versions.length
  );

  return {
    artifact: combinedData?.artifact,
    versions,
    columns,
    searchKey,
    isDataLoading,
    shouldRedirect,
    handleStateChange,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setClientFilteredCount,
  };
}
